import React, {useEffect, useMemo, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import { useAuth, useOrders } from '../context';
import './OrdersList.css';

const formatDate = (value) => {
    if (!value) return '';
    try {
        // If numeric (string or number), treat as unix timestamp in seconds or milliseconds
        const num = typeof value === 'string' && value.trim() !== '' ? Number(value) : (typeof value === 'number' ? value : NaN);
        let date;
        if (!Number.isNaN(num)) {
            // If it's clearly seconds (below 1e12), convert to ms
            const ms = num < 1e12 ? num * 1000 : num;
            date = new Date(ms);
        } else if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'string') {
            // ISO or other date string
            date = new Date(value);
        }
        if (!date || isNaN(date.getTime())) return '';
        return date.toLocaleString();
    } catch {
        return '';
    }
};

const OrdersList = () => {
    const {user} = useAuth();
    const {listOrders} = useOrders();

    const location = useLocation();

    const [orders, setOrders] = useState([]);
    const placedId = location.state?.placed;

    const refresh = useMemo(() => {
        return () => {
            if (user?.id) {
                setOrders(listOrders(user.id));
            } else {
                setOrders([]);
            }
        };
    }, [user?.id, listOrders]);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    if (!user) return null;

    return (
        <div className="orders-list">
            <header className="orders-header-nav">
                <Link to="/account" className="btn btn-link">Back to Account</Link>
            </header>
            
            <h1 className="orders-title">My Orders</h1>

            {placedId && (
                <div className="order-success-alert">
                    Order placed successfully. {String(placedId).startsWith('ORD-') ? `Order #${placedId}` : ''}
                </div>
            )}

            {orders.length === 0 ? (
                <div className="orders-empty">
                    <p>You don’t have any orders yet.</p>
                    <Link to="/shop">Start shopping</Link>
                </div>
            ) : (
                <div className="orders-grid">
                    {orders.map((o, idx) => (
                        <div key={`${o.id || o.orderNumber || 'order'}-${idx}`} className="order-card">
                            <div className="order-header">
                                <div className="order-info">
                                    <div className="order-number">{o.orderNumber}</div>
                                    {o.shippingNumber && (
                                        <div className="order-shipping">Shipping #: {o.shippingNumber}</div>
                                    )}
                                    {o.created_at ? (
                                        <div className="order-date">Placed {formatDate(o.created_at)}</div>
                                    ) : null}
                                </div>
                                <div className="order-summary">
                                    <div className="order-total">${o.total.toFixed(2)}</div>
                                    <div className="order-items-count">
                                        {o.items.reduce((n, it) => n + it.quantity, 0)} items
                                    </div>
                                </div>
                            </div>
                            <div className="order-items-preview">
                                {o.items.slice(0, 4).map((it, idx) => (
                                    <div key={`${it.id}-${idx}`} className="order-item-thumb">
                                        {it.imageUrl ? (
                                            <img src={it.imageUrl} alt={it.name} className="order-item-img" />
                                        ) : (
                                            <div className="order-item-placeholder" />
                                        )}
                                        <div className="order-item-info">
                                            <div className="order-item-name">{it.name}</div>
                                            <div className="order-item-details">
                                                {it.size ? `${it.size} · ` : ''}Qty {it.quantity}
                                                {it.comment ? <div className="order-item-comment">Note: {it.comment}</div> : null}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {o.items.length > 4 && (
                                    <div className="order-more">+{o.items.length - 4} more</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            <footer className="orders-footer">
                <Link to="/account" className="btn btn-link">Back to Account</Link>
            </footer>
        </div>
    );
};

export default OrdersList;
